/**
 * The sync engine: push the outbox, pull what changed, bump the counters.
 *
 * Push and pull are the two halves of replication. Push drains the outbox in
 * order — consecutive creates of one record in one batch, an edit alone in the
 * place it was made, everything else one op at a time — so a transport failure
 * or a 5xx stops the drain and leaves the rest for the next trigger, and a 4xx
 * refusal drops just that op. The decisions live in `outbox-drain.ts`, where a
 * test can reach them; what is left here is the wiring and the counters.
 * Pull pages the server from the stored cursor and applies each page
 * transactionally through the repository.
 *
 * Runs are serialized per tenant: a second trigger while one is in flight
 * sets a dirty flag and gets one more pass afterwards, never a parallel run.
 * That is what lets the events socket, the foreground trigger, the interval,
 * and a form save all call in without interlocking.
 */
import { REGISTRY_VERSION } from '@alluzo/shared';

import { ApiRequestError, createEntries, createEntry, fetchSyncPull, updateEntry } from '@/lib/api';
import { getCookie } from '@/lib/auth';
import * as repo from '@/lib/entry-repo';
import { saveUnpicked } from '@/lib/list-unpicked-store';
import { describeApiFailure } from '@/lib/failure-messages';
import { drainOutbox } from '@/lib/outbox-drain';
import { classifySyncFailure, isTransportFailure } from '@/lib/sync-failure';
import { runSearchBackfill } from '@/lib/search-backfill';
import {
  isLongDownload,
  receivePage,
  saveRows,
  startDownload,
  type DownloadProgress,
} from '@/lib/sync-progress';
import { mirrorFloorDay } from '@/lib/mirror-window';
import { utcToday, workspaceDayOf, workspaceToday } from '@/lib/today-totals';
import { useSyncStore } from '@/stores/sync-store';

/**
 * Drain the outbox in created_at order. Acks stamp the row; 4xx refusals are
 * final for that op and drop it; a 5xx or a transport error stops the drain
 * and leaves the remaining ops for the next trigger.
 */
export const push = async (tenantId: string): Promise<void> => {
  // Only this business's ops, and `listOutbox` refuses to guess: a phone
  // holding two businesses answers nothing to a caller that names none,
  // because ops pushed under the wrong tenant are refused as a 4xx, and a 4xx
  // is final — it would delete somebody else's unsent sales.
  const ops = repo.listOutbox(tenantId);
  if (ops.length === 0) return;
  const cookie = await getCookie();

  // The four side effects, bound to this run's cookie and business. Everything
  // that DECIDES — what is batched, what is retried alone, what is dropped —
  // is in `drainOutbox`, and it takes these so it can be tested without a
  // network or a database.
  const touched = await drainOutbox(ops, {
    one: (op) =>
      createEntry(
        cookie,
        tenantId,
        op.recordId,
        op.values,
        { entryId: op.entryId, clientOpId: op.opId },
        // The entry and its lines are one write: one op, one transaction.
        op.lines,
      ),
    many: (recordId, group) =>
      createEntries(
        cookie,
        tenantId,
        recordId,
        group.map((op) => ({
          entryId: op.entryId,
          clientOpId: op.opId,
          values: op.values,
          ...(op.lines === undefined ? {} : { lines: op.lines }),
        })),
      ),
    // An edit carries only the keys somebody changed, because PATCH is a merge
    // on the server too: re-sending every stored value would write a field
    // another phone corrected in the meantime back to what this one last saw.
    edit: (op) =>
      updateEntry(cookie, tenantId, op.recordId, op.entryId, op.values, { clientOpId: op.opId }, op.lines),
    markSynced: repo.markSynced,
    markSyncedOp: repo.markSyncedOp,
    removePending: repo.removePending,
    dropEditOp: repo.dropEditOp,
    // SAY SO. This used to write the reason into `outbox.failed_reason` and
    // then delete that row on the very next line, and the drain returned
    // normally afterwards — so the run counted as a success,
    // `clearSyncFailure` wiped the banner, and a person who typed a sale
    // watched it save and later found it gone with nothing said. The store
    // keeps it instead, where a refusal survives the healthy runs that follow.
    //
    // With what it carried and the server's word on each field, so the person
    // can open it, put it right and send it again (B10 of the ledger live
    // test): a refused create is deleted from the mirror on the next line.
    noteRefusal: (op, thrown) => {
      useSyncStore.getState().noteRefusedWrite({
        opId: op.opId,
        recordId: op.recordId,
        reason: describeApiFailure(thrown),
        entryId: op.entryId,
        kind: op.kind === 'edit' ? 'edit' : 'create',
        values: op.values,
        ...(op.lines === undefined ? {} : { lines: op.lines }),
        problems: thrown instanceof ApiRequestError ? thrown.problems : [],
      });
    },
    // What the save's links did: said once, and a link that held but could not
    // write is kept until read (L3 of docs/plans/links-fixes.md).
    noteLinks: (op, outcome) => useSyncStore.getState().noteLinks(op.entryId, outcome),
    // What the save's rules held on the server from this device's old copy:
    // said once, as the form says its warns (F14 of docs/plans/field-relations.md).
    noteWarnings: (op, warnings) => useSyncStore.getState().noteWarnings(op.entryId, warnings),
  });

  const store = useSyncStore.getState();
  for (const recordId of touched) store.bumpRecord(recordId);
  store.setPendingCount(repo.outboxCount(tenantId));
};

/**
 * A lost page is asked for again after each of these waits before the run
 * fails. A first download of lakhs of entries is about a hundred pages over
 * some minutes, and one dropped connection in that time used to fail the whole
 * run and draw a red card over the record the person had just opened. Only a
 * transport failure is retried here: a refusal is an answer, and asking again
 * at once will not change it.
 */
const PAGE_RETRY_WAITS_MS: readonly number[] = [1_000, 3_000, 9_000];

/**
 * During a long download, open screens re-read at most this often.
 *
 * A bump makes the entry list and every business-wide number read the mirror
 * again, and a page lands about every second. Once in five seconds shows the
 * rows as they arrive without making the dashboard add up lakhs of rows once
 * a second.
 */
const DOWNLOAD_BUMP_EVERY_MS = 5_000;

/**
 * A page is written this many rows at a time, with a pause between.
 *
 * `applyPulled` is synchronous SQLite on the JavaScript thread, about five
 * statements a row. A 5,000-row page in one transaction held the thread for
 * seconds at a time on a debug build, and a tap on New entry answered eight
 * seconds late. Two hundred and fifty rows is a short hold, and the pause
 * between two of them is where a tap, a scroll and a save get their turn.
 *
 * Each part is its own transaction and moves the cursor to its own last row.
 * That is the same promise the page made: the stream is in server_seq order,
 * so every version at or below that seq has been applied.
 */
const APPLY_ROWS_AT_ONCE = 250;

const wait = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

const fetchPage: typeof fetchSyncPull = async (cookie, tenantId, body) => {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await fetchSyncPull(cookie, tenantId, body);
    } catch (thrown) {
      const delay = PAGE_RETRY_WAITS_MS[attempt];
      if (delay === undefined || !isTransportFailure(thrown)) throw thrown;
      await wait(delay);
    }
  }
};

/**
 * Pull every page since the stored cursor and apply it. Schemas land before
 * the entries that read them; the cursor moves with each page inside the same
 * transaction as that page's entries.
 *
 * A long download, one of more than one page, also does three things a quiet
 * pull does not:
 *
 *   1. It PUSHES between pages and between the parts of a page. A save
 *      during a run only asks for one more run, so a sale typed during a
 *      26-minute first download sat at "Syncing" for all of it. The outbox
 *      now goes up within about one part of a page.
 *   2. It COUNTS. The store holds the rows and bytes so far, and every tab
 *      draws them as a bar. See sync-progress.ts.
 *   3. It BUMPS as it goes, every few seconds, so a record whose rows have
 *      arrived shows them rather than a skeleton until the last page.
 *   4. It FETCHES AHEAD. The next page is asked for before this one is
 *      written, so the server and the network work while the phone writes.
 *      One after the other, a 5,000-row page cost about 2.4 seconds of
 *      waiting on the test server before a single row was written.
 *   5. It leaves the SEARCH INDEX for later. A search document is about four
 *      of the five writes a row costs. The rows go in alone, their records
 *      are marked stale, and the backfill builds the documents in one pass
 *      when the download ends. The search screen says so meanwhile.
 */
const pullPages = async (tenantId: string): Promise<void> => {
  const cookie = await getCookie();
  const store = useSyncStore.getState();
  const since = repo.getSince(tenantId);
  /**
   * The day this run asked the server to start from, or null when it asked for
   * everything.
   *
   * It is sent on every page of a fill, and it is also half the horizon the
   * prune writes at the end: rows older than this never arrive, so no prune can
   * discover their absence by deleting them.
   */
  const fillFrom = since === 0 ? mirrorFloorDay(utcToday()) : null;
  const touched = new Set<string>();
  const unshown = new Set<string>();
  let catalogChanged = false;
  let landedAnything = false;
  let first = true;
  let progress: DownloadProgress | null = null;
  let bumpedAt = Date.now();

  /**
   * One page asked for, with its failure held as a value.
   *
   * A value and not a rejection, because a page fetched ahead is not awaited
   * until the page before it is written: a rejection in that time would be an
   * unhandled one, and a run that fails before it gets there simply drops it.
   */
  type Asked = { readonly got: Awaited<ReturnType<typeof fetchSyncPull>> } | { readonly failed: unknown };
  const ask = (from: number, withRemaining: boolean): Promise<Asked> =>
    fetchPage(cookie, tenantId, {
      since: from,
      /**
       * The window, and it travels on EVERY page of a full fill.
       *
       * A full fill is a hundred pages and only the first carries a zero
       * cursor, so a server that read the window off `since` would shrink one
       * page and send the other ninety-nine whole. `fullFill` says which run
       * this is, and the server honours `fromDay` only under it.
       *
       * Only on a fill, because only a fill is large. On an incremental pull
       * every version past the cursor travels whatever its day, and this
       * device's own prune decides what to keep — which is what stops a row
       * edited OUT of the window from being stranded here at its old value.
       */
      ...(fillFrom === null ? {} : { fullFill: true, fromDay: fillFrom }),
      // Scoped, because this map is SENT: a pull for one business naming
      // another's records asks the server about rows outside the business it
      // named. A page fetched ahead sends the map as it was before the page
      // in front of it landed, so a schema can come twice; caching one twice
      // is harmless. The registry rides with it: the schemas a form draws
      // come from here, and a pull that named none was answered in the floor.
      ...repo.schemaAsk(tenantId),
      // The count costs the server a second read, so only the first page asks.
      withRemaining,
    }).then(
      (got) => ({ got }),
      (failed: unknown) => ({ failed }),
    );

  let asked: Promise<Asked> = ask(since, true);
  let long = false;
  let completed = false;

  try {
    for (;;) {
      if (!first && repo.outboxCount(tenantId) > 0) await push(tenantId);

      const answer = await asked;
      if ('failed' in answer) throw answer.failed;
      const { page, bytes } = answer.got;
      if (page.hasMore) asked = ask(page.nextSince, false);

      // A page that arrived proves the session and the server, so a notice from
      // an earlier run goes now, not at the end of a download that takes
      // minutes. Sign-out raced a pull into a 401 once, and "sign out and sign
      // in again" then sat above a healthy download for all of it.
      store.clearSyncFailure();

      // Decided on the first page, BEFORE it is written, so the first page of
      // a long download skips the index as well.
      if (first) {
        long = isLongDownload(page.hasMore, page.remaining);
        if (long) {
          progress = startDownload({
            tenantId,
            rows: page.entries.length,
            remaining: page.remaining ?? 0,
            bytes,
          });
        }
      } else if (progress !== null) {
        progress = receivePage(progress, { rows: page.entries.length, bytes });
      }
      if (progress !== null) store.setDownload(progress);

      // A record whose form changed is woken like one whose rows did: a form open on it draws the new one.
      for (const recordId of repo.cacheSchemas(page.schemas)) touched.add(recordId);
      // Every schema this business holds is now the one served for this
      // build: a first page asked with none named brought all of them.
      if (first) repo.markSchemasServed(tenantId, REGISTRY_VERSION);
      const ordered = [...page.entries].sort((a, b) => a.serverSeq - b.serverSeq);
      const applied: string[] = [];
      for (let at = 0; ; at += APPLY_ROWS_AT_ONCE) {
        const part = ordered.slice(at, at + APPLY_ROWS_AT_ONCE);
        const last = at + APPLY_ROWS_AT_ONCE >= ordered.length;
        const cursor = last ? page.nextSince : part[part.length - 1]!.serverSeq;
        applied.push(...repo.applyPulled(part, cursor, tenantId, { index: !long }));
        if (progress !== null) {
          progress = saveRows(progress, part.length);
          store.setDownload(progress);
        }
        if (last) break;
        await wait(0);
        // The same pause is where a save made during this page goes up, so an
        // entry waits for 250 rows and not for 5,000.
        if (repo.outboxCount(tenantId) > 0) await push(tenantId);
      }
      for (const recordId of applied) {
        touched.add(recordId);
        unshown.add(recordId);
      }
      // The titles of picked entries the window leaves out, before any screen re-reads the rows that pick them (step 9.8).
      repo.applyPickTitles(page.pickTitles, tenantId);
      // Before the catalog is written: the comparison is with what was stored,
      // and what it finds is stored too, so a run that fails after this page's
      // catalog lands still owes the re-read (see the end of the run).
      const shift = repo.catalogShift(page.records);
      repo.noteReread(tenantId, shift, since === 0);
      for (const recordId of shift.masked) touched.add(recordId);
      // The whole catalog arrives on every page, so it is news only when a row
      // differs from the one stored — `upsertCatalog` says which.
      const catalogMoved = repo.upsertCatalog(page.records, tenantId);
      if (catalogMoved) catalogChanged = true;
      // The Dashboard cards the setup drafted, on every page as the catalog is:
      // news only when they differ from the ones kept.
      if (repo.saveSetupBoardCards(tenantId, page.boardCards ?? [])) store.bumpBusinessInsights();
      // What each total of a list leaves out for picking no row of it: only on
      // the page that catches the device up, and then replaced whole.
      if (page.unpicked !== undefined && saveUnpicked(tenantId, page.unpicked)) store.bumpBusinessInsights();
      // The tenant is not optional here in spirit: `readableRecordIds` is ONE
      // business's answer, so without it the candidate select would offer up
      // every OTHER business's records for deletion. `pruneCatalog` refuses to act
      // at all rather than guess, which is why it is named.
      const pruned = repo.pruneCatalog(page.readableRecordIds, tenantId).length > 0;
      if (pruned) catalogChanged = true;
      // A pruned record is a landing too: its rows left the mirror, and a screen
      // open on it has to re-read to find that out.
      landedAnything =
        landedAnything || applied.length > 0 || catalogMoved || pruned || page.schemas.length > 0;

      if (progress !== null && Date.now() - bumpedAt >= DOWNLOAD_BUMP_EVERY_MS) {
        for (const recordId of unshown) store.bumpRecord(recordId);
        unshown.clear();
        store.bumpCatalog();
        bumpedAt = Date.now();
      }

      /**
       * The buckets, and then the prune — on the page that catches up, and
       * never before it.
       *
       * In this order for one reason: `pruneRecord` deletes a row only when a
       * bucket already covers its day, so the buckets have to be in the table
       * before anything can go. Ordered the other way, the first run after an
       * upgrade would delete nothing and the second would delete everything at
       * once.
       */
      if (!page.hasMore) {
        // Every row of this run is in, and the prune below has not yet taken
        // the ones outside the window: the one moment the count is fair.
        repo.settleReread(tenantId, since === 0);
        const bucketed = repo.applyBuckets(page.buckets, tenantId);
        // The same pause the row apply takes, for the same reason: a first
        // prune after an upgrade has half a million rows to forget, and one
        // uninterrupted loop would hold the thread for minutes.
        const pruned = await repo.pruneMirror(tenantId, utcToday(), fillFrom, () => wait(0));
        for (const recordId of bucketed) unshown.add(recordId);
        for (const outcome of pruned) unshown.add(outcome.recordId);
        if (bucketed.length > 0 || pruned.length > 0) landedAnything = true;
        // The rows are gone; the space they took is not, until this runs.
        await repo.reclaimSpace(pruned.reduce((total, outcome) => total + outcome.removed, 0));
      }

      first = false;
      if (!page.hasMore) break;
    }
    completed = true;
  } finally {
    // Gone on success and on failure alike. A failed run leaves its cursor
    // where the last page put it, and the next run counts from there.
    if (progress !== null) store.setDownload(null);
  }

  // The documents the download skipped. After `setDownload(null)`, because
  // the backfill will not start while a download is in flight. A run that
  // failed part-way leaves its records stale, and the next launch, foreground
  // or finished download builds them.
  if (long && completed) void runSearchBackfill();

  /**
   * Rows the cursor has already passed, which no pull will ever send again
   * (web-redesign-04 phase 3, Decided 4). Three ways a person comes to read
   * rows older than their cursor: a wider reach (Own to Their team, a new
   * report in the team), a record given to them since the last pull, and a
   * hidden field that moved, whose old values and schema this device still
   * holds. For the last, the record's rows, buckets and schema version are
   * forgotten on the page that finds it, so a field set to None stops showing
   * now. Then, once a run finishes, the pull starts again from the beginning,
   * as a fill, and runs once more.
   *
   * What is owed is STORED (`noteReread`, reread.ts), page by page and before
   * that page's catalog is written, because the catalog is what the next run
   * compares with: a run that fails or is killed after the write would
   * otherwise leave nothing to find. Any run that finishes pays it.
   *
   * A record seen for the first time is a re-read only on a phone that had
   * synced this business before, and only when the device holds fewer of its
   * rows than the catalog counts (`givenSince`): a record made since the last
   * pull brings every row with it, and a record given to the person brings
   * only the rows written since, which may be one. Never on the run that IS
   * the re-read: it starts at zero, and by then the catalog it compares with
   * is the one it wrote.
   */
  if (completed && repo.restartIfReread(tenantId)) dirtyTenants.add(tenantId);

  for (const recordId of touched) {
    store.bumpRecord(recordId);
    // An entry moving changes the record's today total too.
    catalogChanged = true;
  }
  if (catalogChanged) store.bumpCatalog();
  store.setPendingCount(repo.outboxCount(tenantId));
  // Stamp the first sync even when it landed nothing — "never synced" is the
  // hooks' loading gate — and afterwards only when something changed, so a
  // quiet 45s interval pull re-renders nothing. Per business: the gate is
  // asked about the business on screen, and a business first opened after a
  // switch has never synced here however long the other one has.
  //
  // **And once when the day turns.** Every screen reads `today` when it
  // re-reads the mirror, and nothing else tells it the date moved: a Dashboard
  // left open across midnight would go on calling yesterday's takings "In
  // today". The day that turns is the workspace's (`workspaceToday`), the day
  // every total on the phone is counted in, so the stamp is read in that zone.
  const stamped = useSyncStore.getState().lastSyncedAt[tenantId];
  const dayTurned = stamped !== undefined && workspaceDayOf(new Date(stamped)) !== workspaceToday();
  if (landedAnything || stamped === undefined || dayTurned) store.stampSynced(tenantId);
};

/** Push, then pull. The one full pass every trigger eventually funnels into. */
const syncNow = async (tenantId: string): Promise<void> => {
  await push(tenantId);
  await pullPages(tenantId);
};

/**
 * Record why a run failed, and in development say the one thing that actually
 * helps when the reason is a 404.
 *
 * A 404 on a route this app calls is not a user problem and not a network
 * problem: it means the running server does not have the route the client
 * expects, which in practice means it is running older code than the repo.
 * `console.error` draws a red LogBox card in Expo dev, unlike `console.warn`,
 * which only bumps a counter in a banner nobody reads. That difference is the
 * whole reason this cost a day rather than a minute.
 */
const recordFailure = (thrown: unknown): void => {
  const { kind, status } = classifySyncFailure(thrown);
  const message = describeApiFailure(thrown);

  if (__DEV__ && status === 404) {
    console.error(
      '[sync] the server answered 404. The code it is running does not have the sync route. ' +
        'It is almost certainly an old process: restart it with `bun run server` from the repo root.',
      thrown,
    );
  } else {
    console.warn('[sync] run failed:', thrown);
  }

  useSyncStore.getState().noteSyncFailure({ kind, status, message });
};

const inFlight = new Map<string, Promise<void>>();
const dirtyTenants = new Set<string>();

const enqueue = (tenantId: string, task: () => Promise<void>): Promise<void> => {
  const running = inFlight.get(tenantId);
  if (running !== undefined) {
    // A trigger during a run asks for one more pass afterwards, never a parallel run.
    dirtyTenants.add(tenantId);
    return running;
  }

  const store = useSyncStore.getState();
  store.setSyncing(true);
  const run = (async () => {
    let next = task;
    for (;;) {
      try {
        await next();
        // A run that got through clears the failure, so a banner disappears
        // without a restart and the backoff resets.
        useSyncStore.getState().clearSyncFailure();
      } catch (thrown) {
        // A failed run is retried by the next trigger; the mirror is untouched
        // by halves because every page applied transactionally. What is NOT
        // fine is failing quietly: this used to be a console.warn and nothing
        // else, so a 404 on the pull route read as a slow network on every
        // screen, for hours. The store now carries it and the screens read it.
        recordFailure(thrown);
      }
      if (!dirtyTenants.delete(tenantId)) break;
      // The follow-up is always a full sync: a save that arrived mid-run has
      // an outbox op to push, and for a bare ping the push half is a no-op
      // read of an empty outbox.
      next = () => syncNow(tenantId);
    }
  })();
  inFlight.set(tenantId, run);
  void run.finally(() => {
    inFlight.delete(tenantId);
    useSyncStore.getState().setSyncing(false);
  });
  return run;
};

/** A pull, serialized with whatever else is running for this tenant. */
export const pull = (tenantId: string): Promise<void> => enqueue(tenantId, () => pullPages(tenantId));

/** Push, then pull. The trigger for a save, a foreground, an interval. */
export const sync = (tenantId: string): Promise<void> => enqueue(tenantId, () => syncNow(tenantId));

/**
 * A local write changed the mirror: bump its record so the open screen
 * re-queries at once, and refresh the pending count. The push itself is
 * `sync`, fired by the caller right after.
 */
export const notifyLocalWrite = (recordId: string): void => {
  const store = useSyncStore.getState();
  store.bumpRecord(recordId);
  // The pick, because a write made on a screen is a write into the business
  // that screen is showing. The ROW's own tenant comes from the record it went
  // into, which `insertPending` reads for itself.
  store.setPendingCount(repo.outboxCount(repo.getActiveTenant()));
};
